import { useState } from "react";
import { supabase } from "../../lib/supabase";
import { Button, Field, ErrorNote, Logo } from "../../components/ui";
import { checkEmail } from "../../lib/validation";

/**
 * Auth failures used to collapse into one message, which hid the difference
 * between a wrong password, an unconfirmed email, and a misconfigured project.
 * Errors should say what happened and what to do about it.
 */
function explain(message: string): string {
  const m = message.toLowerCase();

  if (m.includes("email not confirmed") || m.includes("not confirmed"))
    return "This account exists but the email isn't confirmed. In Supabase, open Authentication, Users, click the user, and confirm the email.";

  if (m.includes("invalid login credentials"))
    return "Wrong email or password. If the account was just created, check that Auto Confirm was switched on.";

  if (m.includes("email logins are disabled") || m.includes("provider is not enabled"))
    return "Email sign-in is switched off for this project. Enable it in Supabase under Authentication, Providers, Email.";

  if (m.includes("failed to fetch") || m.includes("networkerror"))
    return "Couldn't reach Supabase. Check VITE_SUPABASE_URL in .env.local, then restart the dev server.";

  if (m.includes("invalid api key") || m.includes("jwt"))
    return "That anon key isn't valid for this project. Re-copy it from Project Settings, API, then restart the dev server.";

  if (m.includes("rate limit") || m.includes("too many"))
    return "Too many attempts. Wait a minute and try again.";

  return `Sign-in failed: ${message}`;
}

export default function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [emailErr, setEmailErr] = useState<string | null>(null);
  const [formErr, setFormErr] = useState<string | null>(null);
  const [pwTouched, setPwTouched] = useState(false);
  const [busy, setBusy] = useState(false);

  async function submit() {
    const e = checkEmail(email) ?? (email.trim() ? null : "Enter your email.");
    setEmailErr(e);
    if (e) return;

    setBusy(true);
    setFormErr(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      if (error) {
        // Also log the raw error — the explained version is for the person,
        // the console version is for whoever is debugging.
        console.error("Supabase auth error:", error);
        setFormErr(explain(error.message));
      }
    } catch (err) {
      console.error("Supabase auth threw:", err);
      setFormErr(explain(err instanceof Error ? err.message : String(err)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="signin">
      <Logo width={210} />
      <p className="signin-sub">Sign in to your lot.</p>

      {/* A real form, not a div. Safari and iOS only offer to save a password
          when the fields sit inside one that submits — the autocomplete
          attributes alone are not enough, which is why the prompt never
          appeared. */}
      <form onSubmit={(e) => { e.preventDefault(); void submit(); }}>
      <Field
        label="Email"
        type="email"
        inputMode="email"
        autoComplete="username"
        value={email}
        onChange={setEmail}
        error={emailErr}
        onBlur={() => setEmailErr(checkEmail(email))}
      />
      <Field
        label="Password"
        type="password"
        autoComplete="current-password"
        error={pwTouched && !password ? "Enter your password." : null}
        onBlur={() => setPwTouched(true)}
        value={password}
        onChange={setPassword}
      />

      {formErr && <ErrorNote>{formErr}</ErrorNote>}

      <div className="actions">
        {/* type="submit" is what tells the browser this form was completed,
            and what triggers the offer to remember the password. */}
        <Button type="submit" disabled={busy || !email || !password}>
          {busy ? "Signing in" : "Sign in"}
        </Button>
      </div>
      </form>
    </div>
  );
}
